import { useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { FileText, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * Two-step modal:
 *  1) Upload PDF → server returns page count + small thumbs
 *  2) Click pages to select → "Use selected pages" renders them at 200 DPI.
 *
 * onPagesRendered(images: [{page, url}]) is fired with the rendered URLs.
 *  - mode="single": parent treats the first image as the slide's image (rest become new slides).
 *  - mode="bulk":   parent decides.
 */
export default function PdfImportDialog({ open, onOpenChange, onPagesRendered, mode = "single" }) {
  const [step, setStep] = useState("upload"); // upload | pick
  const [uploading, setUploading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [pdfId, setPdfId] = useState(null);
  const [thumbs, setThumbs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [originalName, setOriginalName] = useState("");

  const reset = () => {
    setStep("upload");
    setPdfId(null);
    setThumbs([]);
    setSelected(new Set());
    setOriginalName("");
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a .pdf file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/pdf", fd);
      setPdfId(data.pdf_id);
      setThumbs(data.thumbnails || []);
      setOriginalName(data.original_name || "document");
      // default-select page 1
      setSelected(new Set([1]));
      setStep("pick");
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePage = (n) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(thumbs.map((_, i) => i + 1)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one page");
      return;
    }
    setRendering(true);
    try {
      const pages = Array.from(selected).sort((a, b) => a - b);
      const { data } = await api.post("/uploads/pdf/render", { pdf_id: pdfId, pages });
      toast.success(`Rendered ${data.images.length} page${data.images.length === 1 ? "" : "s"}`);
      onPagesRendered?.(data.images, { originalName, mode });
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Render failed");
    } finally {
      setRendering(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-400" /> Import PDF as image
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {step === "upload"
              ? "Drop in a PDF — each page can become its own ad slide."
              : `${originalName}.pdf · ${thumbs.length} page${thumbs.length === 1 ? "" : "s"}. Pick which to import.`}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-3">
            <Label className="label-eyebrow">PDF file</Label>
            <Input
              data-testid="pdf-import-file"
              type="file"
              accept="application/pdf"
              onChange={onFile}
              disabled={uploading}
              className="bg-zinc-950 border-zinc-800"
            />
            {uploading && <div className="text-xs text-zinc-500">Reading PDF…</div>}
            <p className="text-xs text-zinc-500">Max 50 MB. Pages are rendered at 200 DPI (capped 2400 px wide).</p>
          </div>
        )}

        {step === "pick" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-zinc-400">
                {selected.size} of {thumbs.length} selected
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="pdf-pick-all"
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-blue-400 hover:text-blue-300"
                >
                  Select all
                </button>
                <span className="text-zinc-700">·</span>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-zinc-500 hover:text-zinc-300"
                >
                  Clear
                </button>
              </div>
            </div>
            <div
              data-testid="pdf-pick-grid"
              className="grid grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1"
            >
              {thumbs.map((src, i) => {
                const n = i + 1;
                const on = selected.has(n);
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`pdf-page-${n}`}
                    onClick={() => togglePage(n)}
                    className={`relative rounded-md overflow-hidden border-2 transition-colors group ${
                      on ? "border-blue-500" : "border-zinc-800 hover:border-zinc-700"
                    }`}
                  >
                    <img src={src} alt={`Page ${n}`} className="w-full h-auto block bg-white" />
                    <div className="absolute inset-x-0 bottom-0 bg-zinc-950/85 text-[10px] font-mono py-1 text-center">
                      Page {n}
                    </div>
                    {on && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] grid place-items-center font-bold">
                        ✓
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "pick" && (
            <Button
              data-testid="pdf-pick-submit"
              onClick={submit}
              disabled={rendering || selected.size === 0}
              className="bg-blue-600 hover:bg-blue-500"
            >
              <Upload className="w-4 h-4 mr-2" />
              {rendering ? "Rendering…" : `Use ${selected.size} page${selected.size === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
